import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatError } from "../lib/api";
import { shuffle } from "../lib/shuffle";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Trophy, RotateCcw, Info, User } from "lucide-react";
import ExplanationsPanel from "../components/ExplanationsPanel";

export default function FakeChecklist() {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [meta, setMeta] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [trainerName, setTrainerName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const optLabel = (v) => t(`checklist.opt.${v.toLowerCase()}`);

  useEffect(() => {
    api.get(`/quiz/checklist?lang=${lang}`).then((r) => {
      setMeta(r.data);
      const qs = shuffle(r.data.questions).map((q) => ({ ...q, displayOptions: shuffle(q.options) }));
      setQuestions(qs);
    });
  }, [lang]);

  // Live "correct training" probability = share of answers matching the ideal answer.
  // "Unknown" normally gets the benefit of the doubt (same as "No"), except on flagged
  // items (License, Master Trainer, no cure no pay) where it counts as a red flag ("Yes").
  const liveProb = useMemo(() => {
    if (!questions.length) return 1;
    let matches = 0;
    for (const q of questions) {
      let a = answers[q.id];
      if (a === "Unknown") a = q.unknown_red_flag ? "Yes" : "No";
      if (a === q.ideal) matches += 1;
    }
    return Math.max(1, Math.min(99, Math.round((matches / questions.length) * 100)));
  }, [answers, questions]);

  const answeredCount = Object.keys(answers).length;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/quiz/checklist/submit", {
        answers,
        trainer_name: trainerName || undefined,
        display_name: displayName || undefined,
      });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "trainer_exists") {
        toast.error(t("checklist.duplicateTrainer"));
      } else {
        toast.error(formatError(detail));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (result) return <ChecklistResult result={result} user={user} onRetake={() => { setResult(null); setAnswers({}); }} />;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="mb-8 max-w-3xl">
        <div className="font-mono text-xs uppercase tracking-widest text-teal-700 mb-2">{t("checklist.kicker")}</div>
        <h1 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">{meta?.title}</h1>
        <p className="mt-3 text-sm text-slate-500 leading-relaxed">{meta?.description}</p>
        {meta && <p data-testid="checklist-stats" className="mt-3 text-xs font-mono text-teal-700">{t("expl.stats", { l: meta.lessons ?? meta.count, v: meta.videos ?? 0 })}</p>}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Questions */}
        <div className="lg:col-span-2 space-y-3">
          <div className="mb-4 rounded-xl border-2 border-teal-500/60 bg-teal-50/60 p-4">
            <label htmlFor="trainer-name" className="flex items-center gap-2 text-sm font-semibold text-teal-800 mb-2">
              <User size={16} /> {t("checklist.trainerName")} <span className="font-normal text-teal-700/80">{t("checklist.trainerHint")}</span>
            </label>
            <Input
              id="trainer-name"
              data-testid="trainer-name-input"
              placeholder={t("checklist.trainerPlaceholder")}
              value={trainerName}
              onChange={(e) => setTrainerName(e.target.value)}
              className="h-12 bg-white border-slate-300 text-base shadow-sm focus-visible:ring-teal-500 placeholder:text-slate-400"
            />
          </div>

          {questions.map((q, i) => (
            <div key={q.id} data-testid="checklist-item" className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-baseline gap-2.5">
                <span className="font-mono text-xs text-slate-400 shrink-0">{i + 1}</span>
                <p className="text-[15px] text-slate-800 leading-snug">{q.text}</p>
              </div>
              <div className="mt-3 flex gap-2">
                {q.displayOptions.map((opt) => {
                  const selected = answers[q.id] === opt;
                  return (
                    <button
                      key={opt}
                      data-testid={`checklist-${i}-${opt.toLowerCase()}`}
                      onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt }))}
                      className={`flex-1 h-9 rounded-lg text-sm font-medium border transition-all ${
                        selected ? "border-teal-500 bg-teal-600 text-white" : "border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {optLabel(opt)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Live meter */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-4">{t("checklist.meter")}</div>
            <Meter value={liveProb} />
            <div className="mt-5 text-center">
              <div className="text-sm text-slate-500">{t("checklist.meterLabel")}</div>
              <div className="mt-3 text-xs font-mono text-slate-400">{t("checklist.answeredOf", { n: answeredCount, t: questions.length })}</div>
            </div>

            <p data-testid="meter-range-note" className="mt-4 text-center text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
              {t("checklist.rangeNote")}
            </p>

            <div className="mt-5 pt-5 border-t border-slate-100 text-xs text-slate-500 flex gap-2">
              <Info size={14} className="shrink-0 mt-0.5 text-slate-400" />
              <span>{t("checklist.info")}</span>
            </div>

            {!user && (
              <Input
                data-testid="anon-display-name"
                placeholder={t("quiz.nameOptional")}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="mt-4 h-10 text-sm"
              />
            )}

            <Button
              onClick={submit}
              disabled={submitting || answeredCount === 0}
              data-testid="checklist-submit-btn"
              className="mt-4 w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full h-11"
            >
              {submitting ? t("checklist.calculating") : t("checklist.submit")}
            </Button>
            {!user && (
              <p className="mt-3 text-center text-[11px] text-slate-400">
                <Link to="/login" className="text-teal-700 underline">{t("checklist.loginHint")}</Link>{t("checklist.loginHintTail")}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Meter({ value }) {
  const color = value >= 66 ? "#0d9488" : value >= 40 ? "#d97706" : "#dc2626";
  const r = 52;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <div className="relative w-40 h-40 mx-auto" data-testid="trainer-probability-meter">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#e2e8f0" strokeWidth="10" />
        <motion.circle
          cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: offset }} transition={{ type: "spring", stiffness: 60, damping: 15 }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <div className="font-serif font-bold text-4xl text-slate-900">{value}%</div>
        </div>
      </div>
    </div>
  );
}

function ChecklistResult({ result, user, onRetake }) {
  const { t } = useLanguage();
  const { correct_prob, fake_prob, trainer_name } = result;
  let verdict, tone;
  if (correct_prob >= 66) { verdict = t("checklist.verdict.good"); tone = "text-teal-700 bg-teal-50 border-teal-200"; }
  else if (correct_prob >= 40) { verdict = t("checklist.verdict.mixed"); tone = "text-amber-700 bg-amber-50 border-amber-200"; }
  else { verdict = t("checklist.verdict.bad"); tone = "text-rose-700 bg-rose-50 border-rose-200"; }

  return (
    <div className="max-w-2xl mx-auto px-6 py-16 text-center">
      <Meter value={correct_prob} />
      <div className={`mt-6 inline-block font-medium text-sm border rounded-full px-4 py-1.5 ${tone}`} data-testid="checklist-verdict">
        {verdict}
      </div>
      <h1 className="mt-6 font-serif font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">
        {trainer_name ? `${trainer_name}` : t("checklist.thisTrainer")} {t("checklist.scores")} {correct_prob}%
      </h1>
      <p className="mt-4 text-slate-600 leading-relaxed">
        {t("checklist.resultLine", { c: correct_prob, f: fake_prob })}
        {user
          ? (trainer_name ? t("checklist.savedToLb") : t("checklist.addName"))
          : t("checklist.loginToLb")}
      </p>

      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <Link to="/leaderboards">
          <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6" data-testid="view-leaderboard-btn">
            <Trophy size={16} className="mr-2" /> {t("checklist.trainerLeaderboard")}
          </Button>
        </Link>
        <Button variant="outline" onClick={onRetake} className="rounded-full px-6" data-testid="retake-btn">
          <RotateCcw size={16} className="mr-2" /> {t("checklist.evaluateAnother")}
        </Button>
      </div>

      <ExplanationsPanel quiz="checklist" />
    </div>
  );
}
